import axios from "axios";
import { getAuthHeader } from "../../utils/authHeader";
import {
    PaginatedSocialConnections,
    SocialConnectionMetrics,
    SocialConnectionSortColumn,
    SocialConnectionStatusFilter,
    SortDirection,
} from "./types";

const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/admin/social-connections`;

export interface SocialConnectionFilterParams {
    search?: string;
    platform?: string;
    status?: SocialConnectionStatusFilter;
    sortBy?: SocialConnectionSortColumn;
    sortDirection?: SortDirection;
}

export interface GetSocialConnectionsParams
    extends SocialConnectionFilterParams {
    page?: number;
    limit?: number;
}

const buildParams = ({
    search = "",
    platform = "All",
    status = "all",
    sortBy,
    sortDirection = "desc",
}: SocialConnectionFilterParams): Record<string, string> => {
    const params: Record<string, string> = { status };
    if (search.trim()) params.search = search.trim();
    if (platform && platform !== "All") params.platform = platform;
    if (sortBy) {
        params.sortBy = sortBy;
        params.sortDirection = sortDirection;
    }
    return params;
};

export async function getSocialConnections({
    page = 1,
    limit = 10,
    ...filters
}: GetSocialConnectionsParams = {}): Promise<PaginatedSocialConnections> {
    try {
        const headers = await getAuthHeader();

        const response = await axios.get<PaginatedSocialConnections>(BASE_URL, {
            params: { page, limit, ...buildParams(filters) },
            headers,
        });

        return response.data;
    } catch (error) {
        console.error("Error fetching social connections:", error);
        throw new Error("Error fetching social connections");
    }
}

export async function getSocialConnectionMetrics(): Promise<SocialConnectionMetrics> {
    try {
        const headers = await getAuthHeader();

        const response = await axios.get<SocialConnectionMetrics>(
            `${BASE_URL}/metrics`,
            { headers }
        );

        return response.data;
    } catch (error) {
        console.error("Error fetching social connection metrics:", error);
        throw new Error("Error fetching social connection metrics");
    }
}

// The export route is admin-guarded, so it needs the auth header — a plain link or
// window.open() would hit it unauthenticated. Fetch as a blob and click a synthetic anchor.
export async function exportSocialConnectionsCsv(
    filters: SocialConnectionFilterParams = {}
): Promise<void> {
    try {
        const headers = await getAuthHeader();

        const response = await axios.get<Blob>(`${BASE_URL}/export`, {
            params: buildParams(filters),
            headers,
            responseType: "blob",
        });

        const url = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.download = "social-connections.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Error exporting social connections:", error);
        throw new Error("Error exporting social connections");
    }
}
